import {Alert} from "react-native";
import ThermalPrinterModule from "react-native-thermal-printer";
import i18n from "../../../i18n";
import IminPrinter from "./imin-printer.min"
import {getReceiptPaymentType} from "../../constants/converter";
import {priceFormatter} from "../numbers";
import {getInvoiceDetails, orderNeedsInvoice} from "../invoiceDetails";
import FiscatQRService from "../FiscatQRService";
import {computeReceiptTotals, resolveChargedUnitPrice} from "../vatSummary";


export const paymentTypes = {
    cash: "Készpénz",
    creditCard: "Bankkártya",
    temporary: "FIZETETLEN",
    vipBalance: "VIP egyenleg",
};

const RECEIPT_PAYMENT_TYPE_ALIASES = {
    'Készpénz': 'cash',
    'Bankkártya': 'creditCard',
    'Szép kártya': 'szepCreditCard',
    'Utalvány': 'voucher',
    'VIP egyenleg': 'vipBalance',
    'VIP Számla': 'vipBalance',
    'Személyzeti': 'employeeBalance',
    takeAwayCash: 'cash',
    takeAwayCreditCard: 'creditCard',
    phoneCash: 'cash',
    phoneCreditCard: 'creditCard',
    webshopCash: 'cash',
    webshopCreditCard: 'creditCard',
    netpincerCash: 'cash',
    netpincerCreditCard: 'creditCard',
    woltCreditCard: 'creditCard',
    boltCreditCard: 'creditCard',
    falatozzCash: 'cash',
    falatozzCreditCard: 'creditCard',
    orditCreditCard: 'creditCard',
};

const toFiniteAmount = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeReceiptPaymentType = (paymentType) => {
    if (!paymentType) {
        return '';
    }

    return RECEIPT_PAYMENT_TYPE_ALIASES[paymentType] || paymentType;
};

const resolveReceiptPaymentLabel = (paymentType) => {
    if (!paymentType) {
        return 'Fizetés';
    }

    if (paymentTypes[paymentType]) {
        return paymentTypes[paymentType];
    }

    const normalizedPaymentType = normalizeReceiptPaymentType(paymentType);
    return getReceiptPaymentType(normalizedPaymentType)
        || paymentTypes[normalizedPaymentType]
        || paymentType
        || 'Fizetés';
};

const alignPaymentAmountsToOrderSum = (payments, orderSum) => {
    if (!Array.isArray(payments) || payments.length === 0) {
        return [];
    }

    const targetCents = Math.max(0, Math.round(toFiniteAmount(orderSum) * 100));
    const baseCents = payments.map((payment) => Math.max(0, Math.round(toFiniteAmount(payment?.amount) * 100)));
    const sumCents = baseCents.reduce((sum, amount) => sum + amount, 0);

    if (payments.length === 1) {
        return [{ ...payments[0], amount: targetCents / 100 }];
    }

    if (sumCents <= 0) {
        return payments.map((payment, index) => ({
            ...payment,
            amount: index === payments.length - 1 ? targetCents / 100 : 0,
        }));
    }

    if (sumCents === targetCents) {
        return payments;
    }

    let distributed = 0;
    const adjustedCents = baseCents.map((amount, index) => {
        if (index === baseCents.length - 1) {
            return 0;
        }

        const proportionalAmount = Math.floor((targetCents * amount) / sumCents);
        distributed += proportionalAmount;
        return proportionalAmount;
    });

    adjustedCents[adjustedCents.length - 1] = Math.max(0, targetCents - distributed);

    return payments.map((payment, index) => ({
        ...payment,
        amount: adjustedCents[index] / 100,
    }));
};

// TODO: Don't forget to add "new EscPosCharsetEncoding("Cp852", 6)" to "@happy-gastro/react-native-thermal-printer/android"
function isIPorMACAddress(string) {
    // Regular expressions for matching IP addresses and MAC addresses
    const ipRegex = /^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])$/;
    const macRegex = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/;

    // Check if the string matches either IP address or MAC address regex
    if (ipRegex.test(string)) {
        return "IP";
    } else if (macRegex.test(string)) {
        return "MAC";
    } else {
        return "Neither IP nor MAC Address";
    }
}
export const getPrinterTestData = (printerData) => {
    return (
        "\n\n" +
        "[C]<font size='normal'>Nyomtató észlelve!</font>\n" +
        "[C]============================\n" +
        "[L]" +
        printerData.ip +
        "[R]Név: \n" +
        "[L]Port: " +
        printerData.port +
        "[R]Típus: \n" +
        "[C]\n\n\n\n\n\n\n\n\n\n\n[C]"
    );
};

export const getBigStatisticsData = (data, restaurant, user) => {
    const divider = "[C]-------------------\n";
    //const dateRow = "[C]\n" + "[L]" + new Date().toLocaleDateString() + "[R]" + new Date().toLocaleTimeString() + "\n" + "[L]<u>#" + order.orderCode + "</u>[R]<u>" + user.name.first[0] + " " + user.name.last + "</u>\n" + "[C]\n" + "[C]\n" + "[C]\n" + "[C]\n" + "[C]\n";

    const allOrders = [...data.orders].filter(
        (o) => o.status !== "cancelled" && !o.storno
    );

    const acceptedOrdersWithoutMap = allOrders.filter(
        (o) =>
            o.orderType !== "dineIn" ||
            (o.orderType === "dineIn" && o.status === "closed")
    );

    let acceptedOrders = [];
    acceptedOrdersWithoutMap.forEach((o) => {
        if (o.paymentTypes && o.paymentTypes.length > 0) {
            o.paymentTypes.forEach((pT, i) => {
                acceptedOrders.push({
                    ...o,
                    paymentType: pT.paymentType,
                    orderSum: o.paymentTypes.length === 1 || i === 0 ? o.orderSum : 0,
                    productsSum:
                        o.paymentTypes.length === 1 || i === 0 ? o.productsSum : 0,
                    tipAmount: o.paymentTypes.length === 1 || i === 0 ? o.tipAmount : 0,
                    serviceFeePrice:
                        o.paymentTypes.length === 1 || i === 0 ? o.serviceFeePrice : 0,
                    deliveryFee:
                        o.paymentTypes.length === 1 || i === 0 ? o.deliveryFee : 0,
                });
            });
        } else {
            acceptedOrders.push(o);
        }
    });

    const ordersSum = [...acceptedOrdersWithoutMap].reduce(
        (total, order) => order.orderSum + total,
        0
    );

    const stornoOrders = [...data.orders.filter((o) => o.storno)];
    const stornoSum = [...stornoOrders].reduce(
        (total, order) => +total + order.orderSum,
        0
    );

    const creditCardOrders = [...acceptedOrders].filter(
        (o) => o.paymentType === "creditCard" || o.paymentType === "szepCreditCard"
    );
    const woltCreditCardOrders = [...acceptedOrders].filter(
        (o) => o.paymentType === "creditCard" && o.orderFrom === "wolt"
    );
    const boltCreditCardOrders = [...acceptedOrders].filter(
        (o) => o.paymentType === "creditCard" && o.orderFrom === "bolt"
    );

    const phoneOrders = [...acceptedOrders].filter(
        (o) =>
            o.orderFrom === "phone" &&
            o.orderType !== "dineIn" &&
            o.orderType !== "takeAway"
    );
    const webshopOrders = [...acceptedOrders].filter(
        (o) => o.orderFrom === "webshop"
    );
    const serveeOrders = [...acceptedOrders].filter((o) => o.orderFrom === "app");
    const netpincerOrders = [...acceptedOrders].filter(
        (o) => o.orderFrom === "netpincer"
    );
    const falatozzOrders = [...acceptedOrders].filter(
        (o) => o.orderFrom === "falatozz"
    );
    const woltOrders = [...acceptedOrders].filter((o) => o.orderFrom === "wolt");
    const boltOrders = [...acceptedOrders].filter((o) => o.orderFrom === "bolt");

    const takeAwayOrders = [...acceptedOrders].filter(
        (o) => o.orderType === "takeAway"
    );
    const dineInOrders = [...acceptedOrders].filter(
        (o) => o.orderType === "dineIn"
    );
    const onlineOrders = [...acceptedOrders].filter(
        (o) => o.orderType === "online"
    );

    const takeAwayCreditCard = [...takeAwayOrders].filter(
        (o) => o.paymentType === "creditCard"
    );
    const takeAwayCash = [...takeAwayOrders].filter(
        (o) => o.paymentType === "cash"
    );

    // Dine in orders
    const dineInCreditCard = [...dineInOrders].filter(
        (o) => o.paymentType === "creditCard"
    );
    const dineInCash = [...dineInOrders].filter((o) => o.paymentType === "cash");

    const onlineCreditCard = [...onlineOrders].filter(
        (o) => o.paymentType === "creditCard"
    );
    const onlineCash = [...onlineOrders].filter((o) => o.paymentType === "cash");
    const orditOrders = [...acceptedOrders].filter(
        (o) => o.orderFrom === "ordit"
    );

    const falatozzCreditCard = falatozzOrders.filter(
        (o) => o.paymentType === "creditCard"
    );
    const falatozzCash = falatozzOrders.filter((o) => o.paymentType === "cash");
    const pendingOrders = allOrders.filter((o) => o.status === "preparing");

    const cashOrders = [...acceptedOrders].filter(
        (o) => o.paymentType === "cash"
    );
    const vipOrders = [...acceptedOrders].filter(
        (o) => o.paymentType === "vipBalance"
    );
    const voucherOrders = [...acceptedOrders].filter(
        (o) => o.paymentType === "voucher"
    );
    const couponOrders = [...acceptedOrders].filter(
        (o) => o.paymentType === "coupon"
    );
    const szepCardOrders = [...acceptedOrders].filter(
        (o) => o.paymentType === "szepCreditCard"
    );
    const foodoraOrders = [...acceptedOrders].filter(
        (o) => o.orderFrom === "foodora" || o.orderFrom === "netpincer"
    );
    const placeCreditCards = acceptedOrders.filter(
        (o) =>
            o.paymentType === "creditCard" &&
            o.orderFrom !== "wolt" &&
            o.orderFrom !== "netpincer" &&
            o.orderFrom !== "bolt" &&
            o.orderFrom !== "ordit" &&
            o.orderFrom !== "falatozz"
    );
    const platformOrders = [
        ...phoneOrders,
        ...webshopOrders,
        ...serveeOrders,
        ...netpincerOrders,
        ...falatozzOrders,
        ...woltOrders,
        ...boltOrders,
        ...takeAwayOrders,
        ...orditOrders,
    ];


    const userName = user?.name ? ((user.name.first || '') + ' ' + (user.name.last || '')).trim() : '';
    const restaurantHeader =
        "[C]<font size='big'>" + (restaurant?.name || 'Statisztika') + "</font>\n" +
        "[C]================================\n" +
        "[L]" + new Date().toLocaleDateString() + "[R]" + new Date().toLocaleTimeString() + "\n" +
        (userName ? "[L]Pénztáros:[R]" + userName + "\n" : "") +
        "[C]================================\n\n";

    const header1 = "[C]<font size='big'>Forgalmi adatok</font>\n\n";
    const block1 =
        "[L]Termék forgalom:[R]" +
        priceFormatter(data.priceSummary.productsSum) +
        "\n" +
        "[L]Kedvezmények:[R]" +
        priceFormatter(data.priceSummary.discountsSum) +
        "\n" +
        "[L]Szervízdíj:[R]" +
        priceFormatter(data.priceSummary.serviceFeeSum) +
        "\n" +
        "[L]Szállítási díj:[R]" +
        priceFormatter(data.priceSummary.deliveryFeeSum) +
        "\n" +
        "[L]<b>Végösszeg:</b>[R]<b>" +
        priceFormatter(data.priceSummary.ordersSum) +
        "</b>\n";

    const header2 = "\n[C]<font size='big'>Áfa bontás</font>\n\n";
    const block2 =
        "[L]<font size='small'></font>[C]<font size='small'>Nettó</font>[R]<font size='small'><font size='small'>Bruttó</font>\n" +
        Object.keys(data.vatSummary.vat)
            .map(
                (vatKey) =>
                    "[L]<font size='small'>" +
                    vatKey +
                    "%[C]<font size='small'>" +
                    priceFormatter(data.vatSummary.net[vatKey]) +
                    "</font>[R]<font size='small'><b>" +
                    priceFormatter(data.vatSummary.gross[vatKey]) +
                    "</b></font>"
            )
            .join("\n") +
        "\n";

    const header3 = "\n[C]<font size='big'>Platformok</font>\n\n";
    const block3 =
        "[L]FoodOra:[C]" +
        netpincerOrders.length +
        " db[R]" +
        priceFormatter([...netpincerOrders].reduce((a, b) => +a + b.orderSum, 0)) +
        "\n" +
        "[L]Wolt:[C]" +
        woltOrders.length +
        " db[R]" +
        priceFormatter([...woltOrders].reduce((a, b) => +a + b.orderSum, 0)) +
        "\n" +
        "[L]Bolt:[C]" +
        boltOrders.length +
        " db[R]" +
        priceFormatter([...boltOrders].reduce((a, b) => +a + b.orderSum, 0)) +
        "\n" +
        "[L]Falatozz:[C]" +
        falatozzOrders.length +
        " db[R]" +
        priceFormatter([...falatozzOrders].reduce((a, b) => +a + b.orderSum, 0)) +
        "\n" +
        "[L] -Bankkártya:[R]" +
        priceFormatter([...falatozzCreditCard].reduce((a, b) => +a + b.orderSum, 0)) +
        "\n" +
        "[L] -Készpénz:[R]" +
        priceFormatter([...falatozzCash].reduce((a, b) => +a + b.orderSum, 0)) +
        "\n" +
        "[L]Ordit:[C]" +
        orditOrders.length +
        " db[R]" +
        priceFormatter([...orditOrders].reduce((a, b) => +a + b.orderSum, 0)) +
        "\n" +
        "[L]Servee:[C]" +
        serveeOrders.length +
        " db[R]" +
        priceFormatter([...serveeOrders].reduce((a, b) => +a + b.orderSum, 0)) +
        "\n" +
        "[L]Házhozszállítás:[C]" +
        onlineOrders.length +
        " db[R]" +
        priceFormatter([...onlineOrders].reduce((a, b) => +a + b.orderSum, 0)) +
        "\n" +
        "[L] -Bankkártya:[R]" +
        priceFormatter([...onlineCreditCard].reduce((a, b) => +a + b.orderSum, 0)) +
        "\n" +
        "[L] -Készpénz:[R]" +
        priceFormatter([...onlineCash].reduce((a, b) => +a + b.orderSum, 0)) +
        "\n" +
        "[L]Elvitelre:[C]" +
        takeAwayOrders.length +
        " db[R]" +
        priceFormatter([...takeAwayOrders].reduce((a, b) => +a + b.orderSum, 0)) +
        "\n" +
        "[L] -Bankkártya:[R]" +
        priceFormatter(
            [...takeAwayCreditCard].reduce((a, b) => +a + b.orderSum, 0)
        ) +
        "\n" +
        "[L] -Készpénz:[R]" +
        priceFormatter([...takeAwayCash].reduce((a, b) => +a + b.orderSum, 0)) +
        "\n" +
        "[L]Webshop:[C]" +
        webshopOrders.length +
        " db[R]" +
        priceFormatter([...webshopOrders].reduce((a, b) => +a + b.orderSum, 0)) +
        "\n" +
        "[L] -Bankkártya:[R]" +
        priceFormatter(
            [...webshopOrders]
                .filter((o) => o.paymentType === "creditCard")
                .reduce((a, b) => +a + b.orderSum, 0)
        ) +
        "\n" +
        "[L] -Készpénz:[R]" +
        priceFormatter(
            [...webshopOrders]
                .filter((o) => o.paymentType === "cash")
                .reduce((a, b) => +a + b.orderSum, 0)
        ) +
        "\n" +
        "[L]Telefon:[C]" +
        phoneOrders.length +
        " db[R]" +
        priceFormatter([...phoneOrders].reduce((a, b) => +a + b.orderSum, 0)) +
        "\n" +
        "[L] -Bankkártya:[R]" +
        priceFormatter(
            [...phoneOrders]
                .filter((o) => o.paymentType === "creditCard")
                .reduce((a, b) => +a + b.orderSum, 0)
        ) +
        "\n" +
        "[L] -Készpénz:[R]" +
        priceFormatter(
            [...phoneOrders]
                .filter((o) => o.paymentType === "cash")
                .reduce((a, b) => +a + b.orderSum, 0)
        ) +
        "\n" +
        "[L]<b>Összesen:</b>[R]<b>" +
        priceFormatter([...platformOrders].reduce((a, b) => +a + b.orderSum, 0)) +
        "</b>\n";

    const header4 = "\n[C]<font size='big'>Rendelés típusa</font>\n\n";
    const block4 =
        "[L]Házhozszállítás:[C]" +
        onlineOrders.length +
        " db[R]" +
        priceFormatter(onlineOrders.reduce((a, b) => +a + b.orderSum, 0)) +
        "\n" +
        "[L]-Bankkártya:[C]" +
        onlineCreditCard.length +
        " db[R]" +
        priceFormatter(onlineCreditCard.reduce((a, b) => +a + b.orderSum, 0)) +
        "\n" +
        "[L]-Készpénz:[C]" +
        onlineCash.length +
        " db[R]" +
        priceFormatter(onlineCash.reduce((a, b) => +a + b.orderSum, 0)) +
        "\n" +
        "[L]Elvitelre:[C]" +
        takeAwayOrders.length +
        " db[R]" +
        priceFormatter(takeAwayOrders.reduce((a, b) => +a + b.orderSum, 0)) +
        "\n" +
        "[L]-Bankkártya:[C]" +
        takeAwayCreditCard.length +
        " db[R]" +
        priceFormatter(takeAwayCreditCard.reduce((a, b) => +a + b.orderSum, 0)) +
        "\n" +
        "[L]-Készpénz:[C]" +
        takeAwayCash.length +
        " db[R]" +
        priceFormatter(takeAwayCash.reduce((a, b) => +a + b.orderSum, 0)) +
        "\n" +
        "[L]Helyben:[C]" +
        dineInOrders.length +
        " db[R]" +
        priceFormatter(dineInOrders.reduce((a, b) => +a + b.orderSum, 0)) +
        "\n" +
        "[L]-Bankkártya:[C]" +
        dineInCreditCard.length +
        " db[R]" +
        priceFormatter(dineInCreditCard.reduce((a, b) => +a + b.orderSum, 0)) +
        "\n" +
        "[L]-Készpénz:[C]" +
        dineInCash.length +
        " db[R]" +
        priceFormatter(dineInCash.reduce((a, b) => +a + b.orderSum, 0)) +
        "\n" +
        "[L]<b>Összesen:</b>[C]" +
        acceptedOrders.length +
        " db[R]<b>" +
        priceFormatter(acceptedOrders.reduce((a, b) => +a + b.orderSum, 0)) +
        "</b>\n";

    const cashSum = cashOrders.reduce((a, b) => +a + b.orderSum, 0);
    const placeCreditCardSum = placeCreditCards.reduce((a, b) => +a + b.orderSum, 0);
    const foodoraCardSum = [...foodoraOrders].filter(o => o.paymentType === "creditCard").reduce((a, b) => +a + b.orderSum, 0);
    const woltCardSum = woltCreditCardOrders.reduce((a, b) => +a + b.orderSum, 0);
    const boltCardSum = boltCreditCardOrders.reduce((a, b) => +a + b.orderSum, 0);
    const orditSum = orditOrders.reduce((a, b) => +a + b.orderSum, 0);
    const vipSum = vipOrders.reduce((a, b) => +a + b.orderSum, 0);
    const szepCardSum = szepCardOrders.reduce((a, b) => +a + b.orderSum, 0);
    const voucherSum = voucherOrders.reduce((a, b) => +a + b.orderSum, 0);
    const couponSum = couponOrders.reduce((a, b) => +a + b.orderSum, 0);
    const paymentTotal = cashSum + placeCreditCardSum + foodoraCardSum + woltCardSum + boltCardSum + orditSum + vipSum + szepCardSum + voucherSum + couponSum;

    const header5 = "\n[C]<font size='big'>Fizetési mód szerint</font>\n\n";
    const block5 =
        "[L]Készpénz:[C]" +
        cashOrders.length +
        " db[R]" +
        priceFormatter(cashSum) +
        "\n" +
        "[L]Bankkártya (üzlet):[C]" +
        placeCreditCards.length +
        " db[R]" +
        priceFormatter(placeCreditCardSum) +
        "\n" +
        "[L]FoodOra bankkártya:[C]" +
        foodoraOrders.filter(o => o.paymentType === "creditCard").length +
        " db[R]" +
        priceFormatter(foodoraCardSum) +
        "\n" +
        "[L]Wolt bankkártya:[C]" +
        woltCreditCardOrders.length +
        " db[R]" +
        priceFormatter(woltCardSum) +
        "\n" +
        "[L]Bolt bankkártya:[C]" +
        boltCreditCardOrders.length +
        " db[R]" +
        priceFormatter(boltCardSum) +
        "\n" +
        "[L]Ordit:[C]" +
        orditOrders.length +
        " db[R]" +
        priceFormatter(orditSum) +
        "\n" +
        "[L]SZÉP kártya:[C]" +
        szepCardOrders.length +
        " db[R]" +
        priceFormatter(szepCardSum) +
        "\n" +
        "[L]VIP egyenleg:[C]" +
        vipOrders.length +
        " db[R]" +
        priceFormatter(vipSum) +
        "\n" +
        "[L]Utalvány:[C]" +
        voucherOrders.length +
        " db[R]" +
        priceFormatter(voucherSum) +
        "\n" +
        "[L]Kupon:[C]" +
        couponOrders.length +
        " db[R]" +
        priceFormatter(couponSum) +
        "\n" +
        "[L]<b>Összesen:</b>[C]" +
        acceptedOrders.length +
        " db[R]<b>" +
        priceFormatter(paymentTotal) +
        "</b>\n";

    // Delivery statistics
    const deliveryOrders = allOrders.filter((o) => o.delivery && o.delivery.user);
    const deliveryFeeTotal = allOrders.reduce((total, o) => total + Number(o.deliveryFee || 0), 0);

    // Group deliveries by driver
    const deliveryDrivers = {};
    deliveryOrders.forEach((o) => {
        const driverName = (o.delivery?.user?.name?.first || '') + ' ' + (o.delivery?.user?.name?.last || '');
        const driverId = o.delivery?.user?._id || driverName;
        if (!deliveryDrivers[driverId]) {
            deliveryDrivers[driverId] = { name: driverName.trim() || 'Ismeretlen', count: 0, sum: 0 };
        }
        deliveryDrivers[driverId].count += 1;
        deliveryDrivers[driverId].sum += Number(o.orderSum || 0);
    });

    const headerDelivery = "\n[C]<font size='big'>Kiszállítás</font>\n\n";
    const blockDelivery =
        "[L]Kiszállított rendelések:[R]" +
        deliveryOrders.length +
        " db\n" +
        "[L]Szállítási díj összesen:[R]" +
        priceFormatter(deliveryFeeTotal) +
        "\n" +
        (Object.values(deliveryDrivers).length > 0
            ? Object.values(deliveryDrivers)
                .map(
                    (driver) =>
                        "[L] " +
                        driver.name +
                        ":[C]" +
                        driver.count +
                        " db[R]" +
                        priceFormatter(driver.sum)
                )
                .join("\n") + "\n"
            : "") +
        "[L]<b>Kiszállítás összesen:</b>[R]<b>" +
        priceFormatter(deliveryOrders.reduce((a, b) => +a + Number(b.orderSum || 0), 0)) +
        "</b>\n";

    // Tips
    const tipTotal = acceptedOrdersWithoutMap.reduce((total, o) => total + Number(o.tipAmount || 0), 0);
    const tipDineIn = acceptedOrdersWithoutMap
        .filter((o) => o.orderType === "dineIn")
        .reduce((total, o) => total + Number(o.tipAmount || 0), 0);
    const tipTakeAway = acceptedOrdersWithoutMap
        .filter((o) => o.orderType === "takeAway")
        .reduce((total, o) => total + Number(o.tipAmount || 0), 0);
    const tipOnline = acceptedOrdersWithoutMap
        .filter((o) => o.orderType === "online")
        .reduce((total, o) => total + Number(o.tipAmount || 0), 0);

    const headerTips = "\n[C]<font size='big'>Borravaló</font>\n\n";
    const blockTips =
        "[L]Helyben:[R]" +
        priceFormatter(tipDineIn) +
        "\n" +
        "[L]Elvitelre:[R]" +
        priceFormatter(tipTakeAway) +
        "\n" +
        "[L]Házhozszállítás:[R]" +
        priceFormatter(tipOnline) +
        "\n" +
        "[L]<b>Borravaló összesen:</b>[R]<b>" +
        priceFormatter(tipTotal) +
        "</b>\n";

    const header6 = "\n[C]<font size='big'>Egyéb adatok</font>\n\n";
    const block6 =
        "[L]Tétel stornó:[C]" +
        data.removedItems +
        " db[R]" +
        priceFormatter(data.removedItemsPrice) +
        "\n" +
        "[L]Számla stornó:[C]" +
        stornoOrders.length +
        " db[R]" +
        priceFormatter(stornoSum) +
        "\n" +
        "[L]Összes nyugta:[C]" +
        acceptedOrders.length +
        " db[R]" +
        priceFormatter(acceptedOrders.reduce((a, b) => +a + b.orderSum, 0)) +
        "\n" +
        "[L]Vendég / Kosár:[C]" +
        acceptedOrders.length +
        " db[R]" +
        priceFormatter(ordersSum / acceptedOrders.length) +
        "\n" +
        "[L]Függő rendelések:[C]" +
        data.orders.filter((o) => o.status === "pending").length +
        " db[R]" +
        priceFormatter(
            data.orders
                .filter((o) => o.status === "pending")
                .reduce((a, b) => +a + b.orderSum, 0)
        ) +
        "\n" +
        "[L]<b>Összesen:</b>[C]" +
        acceptedOrders.length +
        " db[R]<b>" +
        priceFormatter(0) +
        "</b>\n\n\n\n";

    return (
        restaurantHeader +
        header1 +
        block1 +
        header2 +
        block2 +
        header3 +
        block3 +
        header4 +
        block4 +
        header5 +
        block5 +
        headerDelivery +
        blockDelivery +
        headerTips +
        blockTips +
        header6 +
        block6 +
        divider
    );
};

export const getBigTerminalData = (order, restaurant, user) => {
    const fontSizes = restaurant.name === "Maharaja étterem" ? "big" : "big";

    const normalizeId = (value) => {
        if (!value) return '';
        if (typeof value === 'string') return value;
        if (typeof value === 'number') return String(value);
        if (typeof value === 'object') return value._id || value.id || value.$oid || '';
        return '';
    };

    const resolveItemName = (item = {}) => {
        // Prefer Hungarian name over remoteName for kitchen printing
        if (item?.name?.hu) return item.name.hu;
        if (item?.item?.name?.hu) return item.item.name.hu;
        if (typeof item?.remoteName === 'string' && item.remoteName.trim()) return item.remoteName;
        if (item?.name?.en) return item.name.en;
        if (item?.item?.name?.en) return item.item.name.en;
        if (typeof item?.name === 'string' && item.name.trim()) return item.name;
        if (typeof item?.item?.name === 'string' && item.item.name.trim()) return item.item.name;
        return 'Tétel';
    };

    const resolveExtraName = (extra = {}) => {
        if (typeof extra?.item?.name === 'string' && extra.item.name.trim()) return extra.item.name;
        if (extra?.item?.name?.hu || extra?.item?.name?.en) return extra?.item?.name?.hu || extra?.item?.name?.en;
        if (typeof extra?.name === 'string' && extra.name.trim()) return extra.name;
        return 'Extra';
    };

    const resolveItemSize = (item = {}) => {
        const nestedSizes = Array.isArray(item?.item?.sizes) ? item.item.sizes : [];
        const directSizes = Array.isArray(item?.sizes) ? item.sizes : [];
        const sizes = nestedSizes.length > 0 ? nestedSizes : directSizes;

        if (!sizes.length) {
            return {
                amount: item?.amountValue || item?.sizeAmount || '',
                unit: item?.unit || item?.sizeUnit || '',
            };
        }

        const targetSizeId = normalizeId(item?.size);
        const explicitIndex = typeof item?.sizeIndex === 'number'
            ? item.sizeIndex
            : (typeof item?.sizeIndex === 'string' && item.sizeIndex !== '' && !Number.isNaN(Number(item.sizeIndex)) ? Number(item.sizeIndex) : null);

        if (targetSizeId) {
            const matchedSize = sizes.find((size) => normalizeId(size?._id || size?.id) === targetSizeId);
            if (matchedSize) {
                return matchedSize;
            }
        }

        if (explicitIndex !== null && sizes[explicitIndex]) {
            return sizes[explicitIndex];
        }

        if (typeof item?.size === 'number' && sizes[item.size]) {
            return sizes[item.size];
        }

        return sizes[0] || {};
    };

    const resolveItemNotes = (item = {}) => {
        return [
            item?.comment,
            item?.serveType,
            ...(Array.isArray(item?.comments) ? item.comments : []),
        ].filter((note) => typeof note === 'string' && note.trim());
    };

    const productRow = (item) => {
        const currentSize = resolveItemSize(item);
        const itemNotes = resolveItemNotes(item);
        const sizeLabel = [currentSize?.amount, currentSize?.unit].filter(Boolean).join(' ').trim();
        const extrasBlock = Array.isArray(item?.extras) && item.extras.length > 0
            ? "\n" + item.extras
                .map((extra) => `[L]<font size='${fontSizes}'>    + ${resolveExtraName(extra)}</font>`)
                .join("\n")
            : "";
        const notesBlock = itemNotes.length > 0
            ? `\n[L]<font size='${fontSizes}'>    \"${itemNotes.join(', ')}\"</font>`
            : '';

        return (
            "[L]<font size='" +
            fontSizes +
            "'>" +
            (item.amount || item.quantity || 1) +
            " x " +
            resolveItemName(item) +
            (sizeLabel ? ` (${sizeLabel})` : '') +
            "</font>" +
            notesBlock +
            extrasBlock +
            "</font>" +
            "[C]----------------------\n"
        );
    };

    let addressRow = "";
    if (order.orderType === "online" && order.addressInfo) {
        if (
            order.addressInfo &&
            order.addressInfo.zip &&
            order.addressInfo.city &&
            order.addressInfo.address
        ) {
            addressRow = "[C]"+order.addressInfo.zip + " " + order.addressInfo.city;
            addressRow = addressRow + "\n" + order.addressInfo.address;
        } else {
            addressRow =
                order.addressInfo && order.addressInfo.address
                    ? "[C]"+order.addressInfo.address
                    : "";
        }
    } else {
        addressRow = "";
    }
    const divider = "[C]-------------------\n";

    const dateRow =
        "[C]\n" +
        "[L]" +
        new Date().toLocaleDateString() +
        "[R]" +
        new Date().toLocaleTimeString() +
        "\n" +
        "[L]<u>#" +
        order.orderCode +
        "</u>[R]<u>" +
        user?.name?.first +
        " " +
        user?.name?.last +
        "</u>\n" +
        "[C]\n" +
        "[C]\n" +
        "[C]\n" +
        "[C]\n" +
        "[C]\n";

    const footerBlock =
        "[C]\n\n\n[C]<font size='big'>#" +
        order.orderCode +
        "</font>\n" +
        "[C]<u><font size='" +
        (order.orderType === "online" ? "normal" : "big") +
        "'>" +
        (order.orderType === "takeAway" && order.orderFrom
            ? "[C]\n\n[C]<font size='big'>" + getOrderFrom(order.orderFrom) + "</font>\n"
            : "") +
        (order.orderType === "online"
            ? "[C]\n\n[C]<font size='big'>" + getOrderFrom(order.orderFrom) + "</font>\n"
            : "") +
        (order.orderType === "dineIn" ? "Table: " + order.tableName : "") +
        (order.orderType === "online"
            ? "<b>" +
            (order?.userInfo?.name?.first || "") +
            " " +
            (order?.userInfo?.name?.last || "") +
            "</b>"
            : "") +
        "</font></u>\n" +
        (order.orderType === "online" && addressRow
            ? "[C]" + addressRow + "\n"
            : "");

    const groupedItems = [...(order?.items || [])].reduce((acc, item) => {
        const normalizedRound = Number.isFinite(Number(item?.round)) ? Number(item.round) : 0;
        if (!acc[normalizedRound]) {
            acc[normalizedRound] = [];
        }
        acc[normalizedRound].push(item);
        return acc;
    }, {});

    return (
        footerBlock +
        [...Array(21).keys()]
            .map((key) => {
                const itemsInRound = groupedItems[key] || [];
                if (itemsInRound.length > 0) {
                    return (
                        (key > 0 ? "\n[C]" + key + ". kör\n" : "\n") +
                        itemsInRound
                            .map((item) => productRow(item))
                            .join("\n")
                    );
                }
            })
            .join("") +
        "\n" +
        divider +
        dateRow
    );
};

const getOrderType = (orderType, order = {}) => {
    switch (orderType) {
        case 'online':
            return (order?.userInfo?.name?.first || "") + " " + (order?.userInfo?.name?.last || "")
        case 'dineIn':
            return 'Asztal: ' + order.tableName
        case 'takeAway':
            return 'Elvitelre'
    }
}

const getOrderFrom = (orderFrom) => {
    if (orderFrom === 'wolt') return 'Wolt';
    if (orderFrom === 'foodpanda') return 'Foodora';
    if (orderFrom === 'foodPanda') return 'Foodora';
    if (orderFrom === 'foodora') return 'Foodora';
    if (orderFrom === 'netpincer') return 'Foodora';
    if (orderFrom === 'falatozz') return 'Falatozz.hu';
    if (orderFrom === 'ordit') return 'Ordit.hu';
    if (orderFrom === 'webshop') return 'Webshop';
    if (orderFrom === 'phone') return 'Telefon / Phone';
    return 'Elviteles / Take away'
}

function stringToBytes(str) {
    const bytes = [];
    for (let i = 0; i < str.length; ++i) {
        bytes.push(str.charCodeAt(i));
    }
    return bytes;
}

export const getSmallReceiptData = async (order, restaurant, restaurantPlace, user) => {
    const IminPrintInstance = new IminPrinter();

    //初始化连接
    IminPrintInstance.connect("SPI").then(async (isConnect) => {
        if (isConnect) {

            try {
                const printer = IminPrintInstance;
                //alert("Connected iMin Printer!")
                IminPrintInstance.initPrinter();
                IminPrintInstance.printAndFeedPaper(50)
                const _generateItemRow = (row) => {
                    //printer.setTextStyle(1)
                    let currentSize = row.item?.sizes?.find((_) => _._id === row.size);
                    if (!currentSize) {
                        // `sizes` may be absent on a lean/partial food ref — the
                        // optional chain only guards `item`, so an unguarded
                        // `sizes[...]` index threw mid-print.
                        currentSize = row?.item?.sizes?.[row.sizeIndex] || row?.item?.sizes?.[0];
                    }
                    let itemName = (row.item.name['hu'] || row.item.name['en']) + ((currentSize && currentSize.amount !== 1) ? " (" + currentSize.amount + "" + currentSize.unit + ")" : "");
                    printer.printColumnsText([(row.amount || row.quantity || 0) + "x", itemName, priceFormatter(row.remmotePrice || row.price)], [1, 5, 3], [0, 0, 2], [26, 26, 28], 460)

                    //printer.setTextStyle(0)
                    if (row.comment) {
                        printer.printColumnsText(["", '"' + row.comment + '"'], [1, 3], [0, 0], [26, 26], 460)
                    }
                    if (row.serveType) {
                        printer.printColumnsText(["", '"' + row.serveType + '"'], [1, 3], [0, 0], [26, 26], 460)
                    }
                    if (row.comments && row.comments.length > 0) {
                        printer.printColumnsText(["", '"' + row.comments.join(', ') + '"'], [1, 3], [0, 0], [26, 26], 460)
                    }
                    if (row.extras) {
                        row.extras.map((extra => {
                            printer.printColumnsText([" +", (extra.item.name.hu || extra.item.name.en)], [1, 3], [0, 0], [26, 26], 460)
                        }))
                    }
                }
                // Items
                const _generateItems = () => {
                    order.items.map((currentItem) => _generateItemRow(currentItem));
                    printer.setAlignment(2);
                    printer.printText("---------")
                    printer.setAlignment(0);
                    printer.printColumnsText(["Összesen:", priceFormatter(order.productsPrice)], [3, 2], [0, 2], [30, 30], 460)

                    if (order.serviceFeePrice > 0) printer.printColumnsText(["Szervízdíj:", priceFormatter(order.serviceFeePrice)], [3, 2], [0, 2], [30, 30], 460)
                    if (order.deliveryFee > 0) printer.printColumnsText(["Szállítás:", priceFormatter(order.deliveryFee)], [3, 2], [0, 2], [30, 30], 460)
                    if (order.discountPrice > 0) printer.printColumnsText(["Kedvezmény:", priceFormatter(order.discountPrice)], [3, 2], [0, 2], [30, 30], 460)

                    if (order.paymentTypes.length > 0) order.paymentTypes.map((paymentType) => printer.printColumnsText([getReceiptPaymentType(paymentType.paymentType) + ":", priceFormatter(paymentType.amount)], [3, 2], [0, 2], [30, 30], 460))


                    if (order.paymentTypes.length === 0) {
                        if (order.paymentType === "temporary") printer.printColumnsText(["Fizetendő:", priceFormatter(order.orderSum)], [3, 2], [0, 2], [30, 30], 460)
                        if (order.paymentType !== "temporary") printer.printColumnsText([getReceiptPaymentType(order.paymentType) + ":", priceFormatter(order.orderSum)], [3, 2], [0, 2], [30, 30], 460)
                    }
                }
                // Header
                const _generateHeader = () => {
                    //printer.printSingleBitmap(restaurant.logo)
                    printer.setAlignment(1);
                    printer.setTextStyle(1);
                    printer.setTextSize(45);
                    printer.printText(restaurant.name);
                    printer.setTextStyle(0);
                    printer.setTextSize(26);
                    printer.printText(restaurantPlace.contact.phone || restaurant.contact.phone || "");
                    if (restaurantPlace.raw) printer.printText(restaurantPlace.raw || "");
                    printer.setTextSize(26);
                    printer.printText("------------------------------------")
                    printer.setTextSize(38);
                    printer.setTextStyle(1);
                    printer.printText("#" + (order.orderCode || "123-456"));
                    printer.setTextSize(26);

                    printer.setAlignment(1);

                    printer.setTextSize(30);
                    printer.printText(getOrderType(order.orderType || 'takeAway', order));
                    if (order.orderType === 'online') {
                        printer.setTextSize(26);

                        if (order.userInfo && order.userInfo.name && order.userInfo.name.first) printer.printText((order.userInfo.name.first || "") + " " + (order.userInfo.name.last || ""));
                        if (order.userInfo && order.userInfo.phone) printer.printText(order.userInfo.phone);

                        if (order.addressInfo && order.addressInfo.zip && order.addressInfo.city && order.addressInfo.address) {
                            printer.printText(order.addressInfo.zip + " " + order.addressInfo.city);
                            printer.printText(order.addressInfo.address)
                        } else {
                            printer.printText(order.addressInfo && order.addressInfo.address ? order.addressInfo.address : "");
                        }
                        if (order.addressInfo.other && order.addressInfo.other.info) printer.printText(order.addressInfo.other.info);
                    }

                    printer.setAlignment(1);
                    printer.setTextSize(26);
                    printer.setTextStyle(1);
                    if (order.comments && order.comments.length > 0) printer.printText('"' + order.comments.join(', ') + '"');
                    if (order.comment) printer.printText('"' + order.comment + '"');

                    printer.setTextSize(26);
                    //IminPrintInstance.setTextLineSpacing('1.0f');
                }
                _generateHeader();
                printer.printText("------------------------------------")
                _generateItems();
                IminPrintInstance.printAndFeedPaper(40)
                printer.setAlignment(1);
                printer.setTextSize(23);
                printer.setTextStyle(1);
                printer.printText("Köszönjük, hogy a vendégünk volt!")
                printer.setTextSize(22);
                printer.setTextStyle(0);
                printer.printText("KÉSZPÉNZ ÁTVÉTELRE NEM JOGOSÍT!")
                printer.printText("NOT VALID FOR CASH PAYMENT!")
                printer.setTextSize(20);
                printer.printText("Üzemelteti a Happy Solutions™ Éttermi szoftver")
                printer.printText("www.happygastro.hu")
                printer.printAndFeedPaper(100)
            } catch (e) {
                Alert.alert(i18n.t('print_failed_title'), i18n.t('invoice_print_failed_message'));
                console.error("Print receipt error:", e)
                getSmallReceiptData(order, restaurant, restaurantPlace)
            }
        }
    });
}
export const getBigReceiptData = (order, restaurant, restaurantPlace, user) => {

    const resolveItemName = (item = {}) => {
        if (typeof item?.remoteName === 'string' && item.remoteName.trim()) return item.remoteName;
        if (typeof item?.name === 'string' && item.name.trim()) return item.name;
        if (item?.name?.hu || item?.name?.en) return item?.name?.hu || item?.name?.en;
        if (typeof item?.item?.name === 'string' && item.item.name.trim()) return item.item.name;
        if (item?.item?.name?.hu || item?.item?.name?.en) return item?.item?.name?.hu || item?.item?.name?.en;
        return 'Tétel';
    };

    const resolveExtraName = (extra = {}) => {
        if (typeof extra?.item?.name === 'string' && extra.item.name.trim()) return extra.item.name;
        if (extra?.item?.name?.hu || extra?.item?.name?.en) return extra?.item?.name?.hu || extra?.item?.name?.en;
        if (typeof extra?.name === 'string' && extra.name.trim()) return extra.name;
        return 'Extra';
    };

    const getPaymentType = (paymentType, paymentNonce) => {
        if (order.paymentType === "creditCard") {
            if (
                order.orderFrom === "netpincer" ||
                order.orderFrom === "wolt" ||
                order.orderFrom === "bolt" ||
                order.orderFrom === "ordit" ||
                order.orderFrom === "falatozz"
            ) {
                return "FIZETVE!";
            }
            if (paymentType === "szepCreditCard" && !paymentNonce)
                return "Szép kártya / Bankkártya futárnál";
            if (paymentType === "creditCard" && paymentNonce) return "FIZETVE!";
            return paymentTypes[order.paymentType];
        }
        return paymentTypes[order.paymentType];
    };

    const toAmount = (value) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
    };

    const isOnlineOrder = order.orderType === "online";
    const customerName = [order?.userInfo?.name?.first, order?.userInfo?.name?.last]
        .filter((part) => typeof part === 'string' && part.trim())
        .join(' ')
        .trim();
    const customerPhone = order?.userInfo?.mobilePhone || order?.userInfo?.phone || '';
    const addressFirstLine = [order?.addressInfo?.zip, order?.addressInfo?.city]
        .filter((part) => typeof part === 'string' && part.trim())
        .join(' ')
        .trim();
    const addressSecondLine = typeof order?.addressInfo?.address === 'string' ? order.addressInfo.address : '';
    const addressDetails = order?.addressInfo?.other?.info || '';

    const addressRow = [addressFirstLine, addressSecondLine].filter((line) => line && line.trim()).join("\n");

    const divider = "[C]------------------------------------------\n";

    const header =
        "[C]<font size='big'><b>#" +
        (order.orderCode || "") +
        "</b></font>\n" +
        "[C]--------------------------------\n" +
        (isOnlineOrder && customerName
            ? "[C]<font size='big' color='black'>" +
            customerName +
            "</font>\n"
            : "") +
        (isOnlineOrder && addressRow
            ? "[C]" + addressRow + "\n"
            : "") +
        (isOnlineOrder && customerPhone
            ? "[C]Telefon: " +
            customerPhone +
            "\n"
            : "") +
        (isOnlineOrder && addressDetails
            ? "[C]" + addressDetails + "\n"
            : "") +
        (isOnlineOrder && order.comments && order.comments.length > 0
            ? '[C]"' + [...order.comments].join(", ") + '"\n'
            : "") +
        (isOnlineOrder && order.comment
            ? '[C]"' + order.comment + '"\n'
            : "") +
        (isOnlineOrder
            ? "\n[C]<font size='big' color='bg-black'>" +
            getPaymentType(order.paymentType, order.paymentNonce) +
            "</font>\n\n"
            : "") +
        "[C]<font size='big' color='black'>" +
        restaurant?.name +
        "</font>\n" +
        "[C]<font size='normal'>" +
        restaurantPlace?.raw +
        "</font>\n" +
        "[C]\n" +
        "[C]=========================================\n" +
        "[C]Kérdés és panasz esetén értesítendő:\n" +
        "[C]" +
        restaurantPlace?.contact?.phone +
        "\n" +
        "[C]=========================================\n" +
        "[C]\n";

    const productRow = (item) => {
        const allSizes = Array.isArray(item?.item?.sizes)
            ? item.item.sizes
            : (Array.isArray(item?.sizes) ? item.sizes : []);
        const currentSize = allSizes.find((_) => _?._id === item?.size) || allSizes[0] || null;
        const qty = Number(item?.amount || item?.quantity || 1) || 1;
        // Unsent cart lines keep `remotePrice` as the LINE total — resolve the
        // charged unit price exactly like the Fiscat rows so qty × unit is right.
        const unitPrice = resolveChargedUnitPrice(item, qty);
        const extras = Array.isArray(item?.extras) ? item.extras : [];
        const sizeRow = currentSize?.amount || currentSize?.unit
            ? "\n   " + (currentSize?.amount || '') + " " + (currentSize?.unit || '') + " / " + priceFormatter(unitPrice)
            : "";

        return (
            "[L]<font size='normal'>" +
            qty +
            " x " +
            resolveItemName(item) +
            "[R]" +
            priceFormatter(Math.round(qty * unitPrice)) +
            sizeRow +
            (extras.length > 0
                ? "\n" +
                "\n" +
                extras
                    .map(
                        (e) =>
                            "[L]<font size='normal'>    + " +
                            resolveExtraName(e) +
                            (e.price !== undefined ? " (" + (qty > 1 ? qty + " x " : "") + priceFormatter(toAmount(e.remotePrice || e.price)) + ")" : "") +
                            "</font>"
                    )
                    ?.join("\n")
                : "") +
            "</font>"
        );
    };

    /**
     * Legacy totals for orders without priced line items: reverse-derived from
     * the persisted `orderSum` / `productsPrice` / `serviceFeePrice` fields.
     *
     * @returns {{productsAmount:number, discountPercentage:number, discountAmount:number, serviceFeeAmount:number, serviceFeePercent:number, deliveryFeeAmount:number, total:number}} Totals.
     */
    const resolveLegacyTotals = () => {
        const orderSumAmount = toAmount(order.orderSum);
        const rawServiceFeeValue = toAmount(order.serviceFee);
        const explicitServiceFeeAmount = toAmount(order.serviceFeePrice);
        const legacyDeliveryFeeAmount = toAmount(order.deliveryFee);
        const productsAmountFromOrder = toAmount(order.productsPrice);
        const legacyProductsAmount = productsAmountFromOrder > 0
            ? productsAmountFromOrder
            : Math.max(0, orderSumAmount - explicitServiceFeeAmount - legacyDeliveryFeeAmount);

        // Some dine-in/offline payloads only persist service fee in `serviceFee`.
        const derivedServiceFeeAmount = Math.max(0, orderSumAmount - legacyProductsAmount - legacyDeliveryFeeAmount);
        const legacyServiceFeeAmount = explicitServiceFeeAmount > 0
            ? explicitServiceFeeAmount
            : (rawServiceFeeValue > 0 && rawServiceFeeValue <= orderSumAmount ? rawServiceFeeValue : derivedServiceFeeAmount);

        const legacyServiceFeePercent = explicitServiceFeeAmount > 0 && rawServiceFeeValue > 0 && rawServiceFeeValue <= 100
            ? rawServiceFeeValue
            : (legacyServiceFeeAmount > 0 && legacyProductsAmount > 0 ? (legacyServiceFeeAmount / legacyProductsAmount) * 100 : 0);

        const discountsAmount = Math.max(0, toAmount(order?.discounts?.amount) + toAmount(order?.discountAmount));
        const computedOrderSumAmount = Math.max(0, legacyProductsAmount + legacyServiceFeeAmount + legacyDeliveryFeeAmount - discountsAmount);

        return {
            productsAmount: legacyProductsAmount,
            discountPercentage: toAmount(order?.discounts?.percentage),
            discountAmount: discountsAmount,
            serviceFeeAmount: legacyServiceFeeAmount,
            serviceFeePercent: legacyServiceFeePercent,
            deliveryFeeAmount: legacyDeliveryFeeAmount,
            total: orderSumAmount > 0 ? Math.max(orderSumAmount, computedOrderSumAmount) : computedOrderSumAmount,
        };
    };

    // Totals come from the same charged rows the Fiscat register receives, so
    // Termék(ek) + Szervízdíj + Szállítási díj − Kedvezmény = Végösszeg always
    // holds and matches the fiscal receipt. `orderSum` is NOT trusted for this:
    // the local dine-in order carries `cartTotal` there, which the table payment
    // paths pass WITHOUT the szervízdíj — it was subtracted from the products and
    // dropped from the Végösszeg.
    const receiptTotals = computeReceiptTotals(order) || resolveLegacyTotals();
    const {productsAmount, serviceFeeAmount, serviceFeePercent, deliveryFeeAmount} = receiptTotals;
    const finalOrderSumAmount = receiptTotals.total;

    const productsSum =
        "[L]Termék(ek) összesen :[R]" +
        priceFormatter(productsAmount) +
        "\n";

    const discountsRow =
        receiptTotals.discountAmount > 0
            ? "[L]<b>Kedvezmény" +
            (receiptTotals.discountPercentage > 0 ? " (" + receiptTotals.discountPercentage + "%)" : "") +
            " :</b>[R]<b>-" +
            priceFormatter(receiptTotals.discountAmount) +
            "</b>\n"
            : "";

    const deliveryFee =
        deliveryFeeAmount === 0
            ? ""
            : "[L]<b>Szállítási díj :</b>[R]<b>" +
            priceFormatter(deliveryFeeAmount) +
            "</b>\n";

    const serviceFee =
        serviceFeeAmount === 0
            ? ""
            : "[L]<b>Szervízdíj (" +
            Number(serviceFeePercent.toFixed(2)) +
            "%) :</b>[R]<b>" +
            priceFormatter(serviceFeeAmount) +
            "</b>\n";

    const orderSumRow =
        "[L]<font size='normal'><b>Végösszeg :</b></font>[R]<font size='normal'><b>" +
        priceFormatter(finalOrderSumAmount) +
        "</b></font>\n\n";

    const normalizedPaymentRows = Array.isArray(order.paymentTypes) && order.paymentTypes.length > 0
        ? order.paymentTypes.map((payment) => ({
            label: resolveReceiptPaymentLabel(payment?.paymentType),
            amount: toAmount(payment?.amount ?? payment?.paidAmount),
        }))
        : [];

    const adjustedPaymentRows = normalizedPaymentRows.length > 0
        ? alignPaymentAmountsToOrderSum(normalizedPaymentRows, finalOrderSumAmount)
        : [{
            label: resolveReceiptPaymentLabel(order.paymentType),
            amount: finalOrderSumAmount,
        }];

    const paymentRows = adjustedPaymentRows
        .map((payment) => "[L]" + (payment.label || 'Fizetés') + ":[R]" + priceFormatter(toAmount(payment.amount)) + "\n")
        .join('');

    // ÁFA / VAT breakdown table — mirrors the Windows POS receipt so the mobile
    // nyugta looks equally professional: line items grouped per VAT rate with the
    // Nettó / ÁFA / Bruttó columns. Source of truth is FiscatQRService.getVatSummary
    // (same helper the on-screen success/order screens use), which prefers the
    // persisted vatSummary/netSummary/grossSummary maps and otherwise derives the
    // breakdown from the order line items.
    const vatBreakdown = (() => {
        let vatSummary = [];
        try {
            vatSummary = FiscatQRService.getVatSummary(order) || [];
        } catch (e) {
            console.warn("Receipt VAT breakdown skipped:", e?.message || e);
            vatSummary = [];
        }

        if (!Array.isArray(vatSummary) || vatSummary.length === 0) {
            return "";
        }

        const headerRow =
            "[L]<font size='small'><b>ÁFA</b></font>" +
            "[R]<font size='small'><b>Nettó</b></font>" +
            "[R]<font size='small'><b>ÁFA</b></font>" +
            "[R]<font size='small'><b>Bruttó</b></font>\n";

        const rateRows = vatSummary
            .map(
                (row) =>
                    "[L]<font size='small'>" + (row?.label != null ? String(row.label) : String(Number(row?.vatRate) || 0)) + "%</font>" +
                    "[R]<font size='small'>" + priceFormatter(toAmount(row?.net)) + "</font>" +
                    "[R]<font size='small'>" + priceFormatter(toAmount(row?.vat)) + "</font>" +
                    "[R]<font size='small'>" + priceFormatter(toAmount(row?.gross)) + "</font>\n"
            )
            .join("");

        return divider + headerRow + rateRows + "[C]\n";
    })();

    const dateRow =
        "[C]\n" +
        "[L]" +
        new Date().toLocaleTimeString() +
        "[R]" +
        new Date().toLocaleDateString() +
        "\n" +
        "[C]\n";

    // VAT invoice request block — when the customer asked for a számla, surface
    // the billing details on the receipt (printed under the summary / payments).
    const invoiceBlock = (() => {
        if (!orderNeedsInvoice(order)) return "";
        const d = getInvoiceDetails(order) || {};
        const rows = [
            d.companyName ? "[L]Cég:[R]" + d.companyName + "\n" : "",
            d.taxNumber ? "[L]Adószám:[R]" + d.taxNumber + "\n" : "",
            d.address ? "[L]Cím:[R]" + d.address + "\n" : "",
            d.name ? "[L]Név:[R]" + d.name + "\n" : "",
            d.email ? "[L]Email:[R]" + d.email + "\n" : "",
            d.phone ? "[L]Tel.:[R]" + d.phone + "\n" : "",
            d.invoiceNumber ? "[L]Számla:[R]" + d.invoiceNumber + "\n" : "",
        ].join("");
        return (
            divider +
            "[C]<font size='normal'><b>SZÁMLAIGÉNY / SZÁMLÁS RENDELÉS</b></font>\n" +
            "[C]<font size='small'>A vendég ÁFÁS számlát kért</font>\n" +
            "[C]\n" +
            rows +
            "[C]\n"
        );
    })();

    const footerBlock =
        "[C]<font size='normal'>Rendelés száma:</font>\n" +
        "[C]<u><font size='big'>#" +
        order.orderCode +
        "</font></u>\n\n\n" +
        "[C]<font size='normal'>Happy Solutions</font>\n" +
        "[C]<u><font size='normal'>www.happysolutions.hu</font></u>\n\n" +
        "[C]<font size='normal'>Köszönjük, hogy a vendégünk volt!</font>\n" +
        "\n" +
        "[C]<font size='small'><b>KÉSZPÉNZ ÁTVÉTELRE NEM JOGOSÍT!</b></font>\n" +
        "[C]<font size='small'><b>NOT VALID FOR CASH PAYMENT!</b></font>\n" +
        "[C]\n" +
        "[C]\n" +
        "[C]\n";

    return (
        header +
        order.items?.map((item) => productRow(item)).join("\n") +
        "\n" +
        divider +
        productsSum +
        serviceFee +
        deliveryFee +
        discountsRow +
        orderSumRow +
        dateRow +
        paymentRows +
        vatBreakdown +
        invoiceBlock +
        divider +
        footerBlock
    );
};

/**
 * Cash-register COPY of the big receipt ('*** CASH REGISTER COPY ***' header
 * so the copy can never be mistaken for the fiscal receipt itself).
 *
 * Restored from commit a85ab4a6, which deleted it as "redundant or unused"
 * while app/helpers/BackgroundTasks.js kept requiring it at four call sites —
 * the destructured import resolved to undefined and every cash-register copy
 * print crashed with a TypeError since that commit.
 *
 * @param {object} order Order to print.
 * @param {object} restaurant Store document (name/address/VAT header lines).
 * @param {object} restaurantPlace Place document of the printing terminal.
 * @param {object} user Cashier user (printed on the footer).
 * @returns {string} RawBT/ESC-POS formatted receipt-copy markup.
 */
export const getCashRegisterReceiptData = (order, restaurant, restaurantPlace, user) => {
    // Similar to getBigReceiptData but with "CASH REGISTER COPY" header

    const getPaymentType = (paymentType, paymentNonce) => {
        if (order.paymentType === "creditCard") {
            if (
                order.orderFrom === "netpincer" ||
                order.orderFrom === "wolt" ||
                order.orderFrom === "bolt" ||
                order.orderFrom === "ordit" ||
                order.orderFrom === "falatozz"
            ) {
                return "FIZETVE!";
            }
            if (paymentType === "szepCreditCard" && !paymentNonce)
                return "Szép kártya / Bankkártya futárnál";
            if (paymentType === "creditCard" && paymentNonce) return "FIZETVE!";
            return paymentTypes[order.paymentType];
        }
        return paymentTypes[order.paymentType];
    };

    let addressRow = "";
    if (order.orderType === "online" && order.addressInfo) {
        if (
            order.addressInfo &&
            order.addressInfo.zip &&
            order.addressInfo.city &&
            order.addressInfo.address
        ) {
            addressRow = order.addressInfo.zip + " " + order.addressInfo.city;
            addressRow = addressRow + "\n" + order.addressInfo.address;
        } else {
            addressRow = order.addressInfo && order.addressInfo.address ? order.addressInfo.address : "";
        }
    }

    const divider = "[C]------------------------------------------\n";

    const header =
        "[C]<font size='big' color='black'>*** CASH REGISTER COPY ***</font>\n" +
        ((order.orderType === "online" && order.userInfo && order.userInfo.name)
            ? "[C]<font size='big' color='black'>" +
            (order?.userInfo?.name?.first || "") +
            " " +
            (order?.userInfo?.name?.last || "") +
            "</font>\n"
            : "") +
        ((order.orderType === "online" && addressRow)
            ? "[C]" + addressRow + "\n"
            : "") +
        ((order.orderType === "online" && order.userInfo && (order.userInfo.mobilePhone || order.userInfo.phone))
            ? "[C]Telefon: " +
            (order.userInfo.mobilePhone || order.userInfo.phone) +
            "\n"
            : "") +
        (order.orderType === "online" && order.comments && order.comments.length > 0
            ? '[C]"' + [...order.comments].join(", ") + '"\n'
            : "") +
        (order.orderType === "online" && order.comment
            ? '[C]"' + order.comment + '"\n'
            : "") +
        (order.orderType === "online"
            ? "\n[C]<font size='big' color='bg-black'>" +
            getPaymentType(order.paymentType, order.paymentNonce) +
            "</font>\n\n"
            : "") +
        "[C]<font size='big' color='black'>" +
        restaurant?.name +
        "</font>\n" +
        "[C]<font size='normal'>" +
        restaurantPlace?.raw +
        "</font>\n" +
        "[C]\n" +
        "[C]=========================================\n" +
        "[C]Kérdés és panasz esetén értesítendő:\n" +
        "[C]" +
        restaurantPlace?.contact?.phone +
        "\n" +
        "[C]=========================================\n" +
        "[C]\n";

    const productRow = (item) => {
        // Hardened during the a85ab4a6 restore: partial/lean food refs may lack
        // `sizes`, `name` or `extras` — the copy must degrade, never throw
        // mid-print (same class of bug as the sizes[] index in getSmallReceiptData).
        const currentSize = item?.item?.sizes?.find((_) => _._id === item.size)
            || item?.item?.sizes?.[item.sizeIndex]
            || item?.item?.sizes?.[0];
        const itemName = item.remoteName
            || item?.item?.name?.hu
            || item?.item?.name?.en
            || item?.name
            || '';
        const extras = Array.isArray(item.extras) ? item.extras : [];
        const qty = Number(item.amount || item.quantity || 1) || 1;
        const unitPrice = resolveChargedUnitPrice(item, qty);

        return (
            "[L]<font size='normal'>" +
            qty +
            " x " +
            itemName +
            "[R]" +
            priceFormatter(Math.round(qty * unitPrice)) +
            (currentSize
                ? "\n   " + currentSize.amount + " " + currentSize.unit + " / " + priceFormatter(unitPrice)
                : "") +
            (extras.length > 0
                ? "\n" +
                "\n" +
                extras
                    .map(
                        (e) =>
                            "[L]<font size='normal'>    + " +
                            (e?.item?.name?.hu || e?.item?.name?.en || e?.name || '') +
                            (e.price !== undefined ? " (" + priceFormatter(e.price) + ")" : "") +
                            "</font>"
                    )
                    ?.join("\n")
                : "") +
            "</font>"
        );
    };

    const dateRow =
        "[C]\n" +
        "[L]" +
        new Date(order.createdAt).toLocaleDateString() +
        "[R]" +
        new Date(order.createdAt).toLocaleTimeString() +
        "\n" +
        "[L]<u>#" +
        order.orderCode +
        "</u>[R]<u>" +
        (user?.name?.first ? user.name.first[0] : "") +
        " " +
        (user?.name?.last || "") +
        "</u>\n" +
        "[C]\n";

    const items = order.items || [];
    const itemsRows = items.map((item) => productRow(item)).join("\n");

    // Same charged-row totals as the customer receipt / Fiscat register — the
    // raw `orderSum` of a local dine-in order can lack the szervízdíj.
    const copyTotals = computeReceiptTotals(order);
    const copyOrderSum = copyTotals ? copyTotals.total : Number(order.orderSum) || 0;
    const copyServiceFee = copyTotals ? copyTotals.serviceFeeAmount : Number(order.serviceFeePrice) || 0;
    const copyDeliveryFee = copyTotals ? copyTotals.deliveryFeeAmount : Number(order.deliveryFee) || 0;

    const totalRow =
        divider +
        "[L]<font size='big'>Összesen: [R]" +
        priceFormatter(copyOrderSum) +
        "</font>\n" +
        (order.paymentType === "cash" && order.paidAmount
            ? "[L]Fizetett: [R]" + priceFormatter(order.paidAmount) + "\n"
            : "") +
        (order.paymentType === "cash" && order.paidAmount
            ? "[L]Visszajáró: [R]" +
            priceFormatter(Math.max(0, order.paidAmount - copyOrderSum)) +
            "\n"
            : "") +
        (order.paymentTypes && order.paymentTypes.length > 0
            ? order.paymentTypes
                .map(
                    (pT) =>
                        "[L]" +
                        getReceiptPaymentType(pT.paymentType) +
                        ": [R]" +
                        priceFormatter(pT.amount)
                )
                .join("\n") + "\n"
            : "") +
        (order.tipAmount && order.tipAmount > 0
            ? "[L]Borravaló: [R]" + priceFormatter(order.tipAmount) + "\n"
            : "") +
        (copyServiceFee > 0
            ? "[L]Szervízdíj: [R]" + priceFormatter(copyServiceFee) + "\n"
            : "") +
        (copyDeliveryFee > 0
            ? "[L]Kiszállítási díj: [R]" + priceFormatter(copyDeliveryFee) + "\n"
            : "") +
        divider;

    const footer =
        "[C]Köszönjük a vásárlást!\n" +
        "[C]www.happygastro.hu\n" +
        "[C]\n\n\n\n\n\n\n\n\n\n\n[C]";

    return header + dateRow + itemsRows + "\n" + totalRow + footer;
};

export const sendPrinter = async (data, printer) => {

    // return true;
    if (printer.ip && printer.ip !== "" && isIPorMACAddress(printer.ip) === "IP") {
        return ThermalPrinterModule.printTcp({
            ...ThermalPrinterModule.defaultConfig,
            timeout: 1500,
            ...printer,
            printerDpi: 203,
            printerWidthMM: 80,
            printerNbrCharactersPerLine: 42,
            payload: data,
            charset: 'Cp852',
            encodingId: 0
        })
            .then((response) => {
                console.log("Data printed by TCP/IP!");
                return true;
            })
            .catch((error) => {
                Alert.alert(
                    i18n.t('print_error_title'),
                    i18n.t('print_error_message', { errorMessage: error.message, printerIp: printer.ip }),
                    [
                        {
                            text: i18n.t('cancel'),
                            onPress: () => Alert.alert('Cancel Pressed'),
                            style: 'cancel',
                        },
                        {
                            text: i18n.t('retry'),
                            onPress: () => sendPrinter(data, printer),
                            style: 'default',
                        },
                    ],
                );
            });
    } else if ((printer.address && printer.address !== "") || isIPorMACAddress(printer.ip) === "MAC") {
        return ThermalPrinterModule.printBluetooth({
            ...ThermalPrinterModule.defaultConfig,
            ...printer,
            ip: undefined,
            port: undefined,
            macAddress: printer.ip,
            payload: data,
            charset: 'Cp437',
            encodingId: 6
        })
            .then((response) => {
                console.log("Data printed by Bluetooth!");
                return true;
            })
            .catch((error) => {
                console.error('Bluetooth print error:', error)
                Alert.alert(
                    i18n.t('print_error_title'),
                    i18n.t('print_error_message', { errorMessage: error.message, printerIp: printer.ip }),
                    [
                        {
                            text: i18n.t('cancel'),
                            onPress: () => Alert.alert('Cancel Pressed'),
                            style: 'cancel',
                        },
                        {
                            text: i18n.t('retry'),
                            onPress: () => sendPrinter(data, printer),
                            style: 'default',
                        },
                    ],
                );
                return false;
            });
    } else {
    }
};

//import { spawnThread } from 'react-native-multithreading';

export const startBackgroundTask = (action) => {
    action();
};
